import { useMemo } from 'react';
import dayjs from 'lib/dayjs';
import { toNumber } from 'lodash-es';
import { useGetReasonLabel } from 'hooks/business/refundReason/useGetReasonLabel';
import { toUpperCase } from 'utils/toLowerCase';
import type { CancelOrder } from 'services/api/refund';

export interface UseTradeCancelInfoParams {
  trade: Conversation.Trade.Vo;
  storeId: string;
}

export interface TradeCancelInfoData {
  /** 是否应该渲染组件 */
  shouldRender: boolean;
  /** 标题文案 key：退货/退款原因 或 取消信息 */
  isRefundStatus: boolean;
  /** 取消类型（买家/卖家发起） */
  cancelType: Conversation.Trade.Vo['cancelType'];
  /** 格式化后的取消原因文案 */
  cancelReasonLabel: string | undefined;
  /** 格式化后的修改时间 */
  formattedModifiedTime: string | undefined;
  /** 是否已完成 */
  isCanceled: boolean;
  /** 是否显示完成状态标签 */
  showCompletedTag: boolean;
  /** 操作人名称 */
  operationUserName: string | undefined;
  /** 格式化后的操作时间 */
  formattedOperationTime: string | undefined;
  /** 是否显示审批按钮 */
  showApprovalBtns: boolean;
  /** 审批按钮所需参数 */
  approvalBtnParams: {
    internalStoreId: string;
    externalStoreId: CancelOrder['externalStoreId'];
    orderId: CancelOrder['orderId'];
    platform: General.CaselessPlatformTunnel;
    region: General.UpperCaseRegion | undefined;
    operate: Conversation.Trade.IReturnOperationType[] | undefined;
  };
}

export interface UseTradeCancelInfoReturn {
  data: TradeCancelInfoData;
}

/**
 * 订单取消/退款信息业务逻辑 hook
 * 负责处理显示条件判断、数据格式化、操作回调等业务逻辑
 */
export function useTradeCancelInfo(params: UseTradeCancelInfoParams): UseTradeCancelInfoReturn {
  const { trade, storeId } = params;
  const {
    platform,
    tradeStatus,
    cancelType,
    cancelReason,
    originStatus,
    externalTradeId,
    externalShopId,
    cancelTrade,
    modifiedTimeInMs,
    operationUserName,
    operationTime,
    region,
  } = trade;

  const { getReasonLabel } = useGetReasonLabel();

  // 是否应该渲染组件
  const shouldRender = Boolean(cancelType || cancelReason);

  // TikTok 特殊逻辑：cancelTrade 数组长度只能是 0 或 1
  const tikTokRefuseNodeShow = cancelTrade?.[0]?.cancelStatus === '1';

  // 计算派生数据
  const data = useMemo<TradeCancelInfoData>(() => {
    const cancelReasonLabel = cancelReason
      ? getReasonLabel(cancelReason, toUpperCase(platform))
      : undefined;

    const formattedModifiedTime = modifiedTimeInMs
      ? dayjs(toNumber(modifiedTimeInMs)).format('YYYY-MM-DD HH:mm:ss')
      : undefined;

    const formattedOperationTime = operationTime
      ? dayjs(parseInt(operationTime, 10)).format('YYYY-MM-DD HH:mm:ss')
      : undefined;

    const upperOriginStatus = toUpperCase(originStatus);
    const isCanceled = upperOriginStatus === 'CANCELLED' || upperOriginStatus === 'CANCELED';

    return {
      shouldRender,
      isRefundStatus: tradeStatus === 'REFUND',
      cancelType,
      cancelReasonLabel,
      formattedModifiedTime,
      isCanceled,
      showCompletedTag: isCanceled && Boolean(operationUserName && operationTime),
      operationUserName,
      formattedOperationTime,
      showApprovalBtns: originStatus === 'IN_CANCEL' || tikTokRefuseNodeShow,
      approvalBtnParams: {
        internalStoreId: storeId,
        externalStoreId: externalShopId,
        orderId: externalTradeId,
        platform: toUpperCase(platform),
        region,
        operate: cancelTrade?.[0]?.operate,
      },
    };
  }, [
    cancelReason,
    getReasonLabel,
    platform,
    modifiedTimeInMs,
    operationTime,
    originStatus,
    shouldRender,
    tradeStatus,
    cancelType,
    operationUserName,
    tikTokRefuseNodeShow,
    storeId,
    externalShopId,
    externalTradeId,
    region,
    cancelTrade,
  ]);

  return {
    data,
  };
}

export default useTradeCancelInfo;
